import { useState, useCallback, useEffect } from 'react'
import { Head } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  HabitLayout,
  HabitCard,
  HabitForm,
  HabitLogModal,
} from '@/components/habits'
import { SatisfactionPicker } from '@/components/satisfaction'
import { useHabitToggle } from '@/hooks/useHabitToggle'
import type { HabitsIndexProps, Habit } from '@/types'

export default function HabitsIndex({
  habits: initialHabits,
  today, // サーバーから提供される「今日」を使用
  todaySatisfaction = null,
}: HabitsIndexProps) {
  const [habits, setHabits] = useState(initialHabits)
  const [showForm, setShowForm] = useState(false)
  const [editingHabit, setEditingHabit] = useState<Habit | undefined>()

  // props の変更を監視してローカル状態を同期
  useEffect(() => {
    setHabits(initialHabits)
  }, [initialHabits])

  const {
    toggle,
    isPending,
    logModalTarget,
    closeLogModal,
    saveLog,
    uncomplete,
  } = useHabitToggle({
    onError: (error) => {
      console.error('Toggle failed:', error)
    },
  })

  const handleToggle = useCallback(
    (habitId: number) => {
      const habit = habits.find((h) => h.id === habitId)
      if (!habit) return

      toggle(
        habitId,
        today,
        !!habit.completedToday,
        (completed) => {
          setHabits((prev) =>
            prev.map((h) =>
              h.id === habitId ? { ...h, completedToday: completed } : h
            )
          )
        },
        {
          enabled: !!habit.loggingEnabled,
          habitName: habit.name,
          existingLog: habit.todayLog,
        }
      )
    },
    [habits, today, toggle]
  )

  const scheduledHabits = habits.filter((h) => h.scheduledToday)
  const otherHabits = habits.filter((h) => !h.scheduledToday)

  const completedCount = scheduledHabits.filter((h) => h.completedToday).length
  const totalCount = scheduledHabits.length

  return (
    <>
      <Head title="今日の習慣 | Habit Tracker" />

      <HabitLayout
        title="今日の習慣"
        action={
          <Button onClick={() => setShowForm(true)} className="touch-target">
            <Plus className="mr-1 h-5 w-5" />
            <span className="hidden sm:inline">新しい習慣</span>
          </Button>
        }
      >
        {/* Progress */}
        {totalCount > 0 && (
          <div className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-muted-foreground text-sm">今日の進捗</span>
              <span className="text-sm font-medium">
                {completedCount} / {totalCount}
              </span>
            </div>
            <div
              role="progressbar"
              aria-valuenow={Math.round((completedCount / totalCount) * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="今日の進捗"
              className="bg-muted h-2 overflow-hidden rounded-full"
            >
              <div
                className="bg-primary h-full rounded-full transition-all duration-300"
                style={{ width: `${(completedCount / totalCount) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* 満足度バロメーター */}
        <div className="mb-6">
          <SatisfactionPicker value={todaySatisfaction} today={today} />
        </div>

        {/* Scheduled habits */}
        {scheduledHabits.length > 0 ? (
          <div className="space-y-3">
            {scheduledHabits.map((habit) => (
              <HabitCard
                key={habit.id}
                habit={habit}
                checked={!!habit.completedToday}
                pending={isPending(habit.id, today)}
                onToggle={() => handleToggle(habit.id)}
                onEdit={() => {
                  setEditingHabit(habit)
                  setShowForm(true)
                }}
              />
            ))}
          </div>
        ) : (
          <div className="py-12 text-center">
            <p className="text-muted-foreground mb-4">
              今日予定されている習慣はありません
            </p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="mr-1 h-5 w-5" />
              習慣を追加
            </Button>
          </div>
        )}

        {/* Other habits (not scheduled for today) */}
        {otherHabits.length > 0 && (
          <div className="mt-8">
            <h2 className="text-muted-foreground mb-3 text-sm font-medium">
              今日は対象外の習慣（クリックで追加実行）
            </h2>
            <div className="space-y-3">
              {otherHabits.map((habit) => (
                <HabitCard
                  key={habit.id}
                  habit={habit}
                  checked={!!habit.completedToday}
                  pending={isPending(habit.id, today)}
                  extra={true}
                  onToggle={() => handleToggle(habit.id)}
                  onEdit={() => {
                    setEditingHabit(habit)
                    setShowForm(true)
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </HabitLayout>

      {/* Form Modal */}
      {showForm && (
        <HabitForm
          habit={editingHabit}
          onClose={() => {
            setShowForm(false)
            setEditingHabit(undefined)
          }}
        />
      )}

      {/* Log Modal */}
      {logModalTarget && (
        <HabitLogModal
          habitName={logModalTarget.habitName}
          initialData={logModalTarget.existingLog}
          mode={logModalTarget.mode}
          onSave={(data) =>
            saveLog(logModalTarget.habitId, logModalTarget.date, data)
          }
          onSkip={closeLogModal}
          onUncomplete={
            logModalTarget.mode === 'edit'
              ? () => {
                  const { habitId, date } = logModalTarget
                  uncomplete(habitId, date, () => {
                    setHabits((prev) =>
                      prev.map((h) => {
                        if (h.id !== habitId) return h
                        const newDates = (h.completedDates || []).filter(
                          (d) => d !== date
                        )
                        return { ...h, completedDates: newDates }
                      })
                    )
                  })
                }
              : undefined
          }
        />
      )}
    </>
  )
}
